import { Close } from "@mui/icons-material";
import * as MUI from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import usePrimaryColor from "./usePrimaryColor";
export const snackbarSeverity = {
  ERROR: "error",
  SUCCESS: "success",
  INFO: "info",
  WARNING: "warning",
};
const useSnackbar = (title, confirmMessage, cancelMessage, alert) => {
  const [promise, setPromise] = useState(null);
  const [message, setMessage] = useState("");
  const [subTitle, setSubTitle] = useState();
  const [alertType, setAlertType] = useState("info");
  const [confirmationSize, setConfirmationSize] = useState("sm");
  const { t } = useTranslation();

  const [snackBar, setSnackbar] = React.useState({
    message: "",
    open: false,
    severity: "",
    variant: "filled"
  });

  //The snackbar will try to translate a given message from the backend
  //However if there is no translation or it just gives an error code
  //it will use a fallback message instead, depending on the severity of the message
  function handleSnackbar(message, severity, variant = "filled", open, translate = true) {
    const fallbackMessage =
      severity === snackbarSeverity.SUCCESS
        ? t("ActionWasSuccessful")
        : t("SomethingWentWrong");
    const translatedMessage = translate
      ? t(message, { defaultValue: fallbackMessage })
      : message;
    setSnackbar({
      message: translatedMessage,
      severity: String(severity).toLowerCase() ?? snackbarSeverity.SUCCESS,
      open: open ?? true,
      variant: variant,
    });
  }

  function getSnackbar() {
    return snackBar;
  }

  function handleFeedbackClose(event, reason) {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar({
      message: "",
      severity: "",
      open: false,
    });
  }

  const action = (
    <React.Fragment>
      <MUI.IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleFeedbackClose}
      >
        <Close fontSize="small" />
      </MUI.IconButton>
    </React.Fragment>
  );

  const SnackbarFeedback = useMemo(() => {
    return (
      <MUI.Portal>
        <MUI.Snackbar
          sx={{ mt: { xs: 0, sm: 0, md: 5 } }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={snackBar.open}
          autoHideDuration={5000}
          onClose={handleFeedbackClose}
        >
          <MUI.Alert
            severity={snackBar?.severity}
            variant={
              snackBar?.severity === snackbarSeverity.WARNING
                ? "outlined"
                : "filled"
            }
            sx={{ width: "100%", color: "white" }}
            action={action}
          >
            {snackBar?.message}
          </MUI.Alert>
        </MUI.Snackbar>
      </MUI.Portal>
    );
  }, [snackBar.open, snackBar.message, snackBar.severity]);

  const confirm = () =>
    new Promise((resolve, reject) => {
      setPromise({ resolve });
    });

  const handleClose = () => {
    setPromise(null);
  };
  function getAlertType(input_type) {
    setAlertType(input_type);
  }
  function getSubtitle(input_subtitle) {
    setSubTitle(input_subtitle);
  }
  function getMessage(input_message) {
    setMessage(input_message);
  }
  const handleConfirm = () => {
    promise?.resolve(true);
    handleClose();
  };

  const handleCancel = () => {
    promise?.resolve(false);
    handleClose();
  };

  const handleSetConfirmationSize = (size) => {
    setConfirmationSize(size);
  };
  const color = usePrimaryColor();

  function getContainer() {
    if (alert || subTitle) {
      return (
        <MUI.Container>
          {subTitle ? (
            <MUI.DialogTitle variant="button">{subTitle}</MUI.DialogTitle>
          ) : null}
          {alert ? <MUI.Alert severity={alertType}>{alert}</MUI.Alert> : null}
        </MUI.Container>
      );
    }
  }

  const ConfirmationDialog = () => (
    <MUI.Dialog open={promise !== null} fullWidth maxWidth={confirmationSize}>
      <MUI.DialogTitle>{title}</MUI.DialogTitle>
      {getContainer()}
      <MUI.DialogContent>{message}</MUI.DialogContent>
      <MUI.DialogActions>
        <MUI.Button
          onClick={handleConfirm}
          sx={{ bgcolor: color, color: "white" }}
        >
          {confirmMessage}
        </MUI.Button>
        {cancelMessage ? (
          <MUI.Button onClick={handleCancel} color="error">
            {cancelMessage}
          </MUI.Button>
        ) : null}
      </MUI.DialogActions>
    </MUI.Dialog>
  );
  return {
    ConfirmationDialog,
    confirm,
    getMessage,
    getSubtitle,
    getAlertType,
    handleSetConfirmationSize,
    getSnackbar,
    handleSnackbar,
    SnackbarFeedback,
  };
};

export default useSnackbar;
